import { InspectionPhotoDTO, VideoDTO } from '../../../loads/data/LoadDTO';
import { Slide } from 'yet-another-react-lightbox';
import { InlinePhoto } from 'shared/utils/MediaUtils';
import {
  getInspectableUrlsFromVideo,
  isDefaultInspectionPhoto,
  isInspectionPhotoSynced,
} from './inspectionPhotoSync';
import { ViewLoadInspectionNonVehiclePhotoTitle } from './ViewLoadInspectionNonVehiclePhotoTitle';

export function getRenderPhotos(
  photos: InspectionPhotoDTO[],
  videos: VideoDTO[],
  type: 'pickup' | 'delivery',
): Array<
  InspectionPhotoDTO & { is_video?: boolean; duration_in_seconds?: number }
> {
  return orderMedia(photos, videos, type).slice(0, 7);
}

/**
 * Every photo and video for the gallery's grid view, in the same order as
 * `buildMediaSlides`.
 */
export function buildGridMedia(
  photos: InspectionPhotoDTO[],
  videos: VideoDTO[],
  type: 'pickup' | 'delivery',
  isPhotoSyncEnabled: boolean,
): InlinePhoto[] {
  return orderMedia(photos, videos, type).map((media) => {
    const isSynced = isInspectionPhotoSynced({
      url: media.rendered_photo || '',
      thumbnailUrl: media.rendered_photo_thumbnail || '',
    });
    const thumbnailUrl =
      isPhotoSyncEnabled && !isSynced
        ? PHOTO_IN_SYNC_PLACEHOLDER
        : media.rendered_photo_thumbnail || media.rendered_photo || '';

    return {
      id: media.guid,
      // A video's url is the mp4, which the grid can't show as an image.
      url: media.is_video ? thumbnailUrl : media.rendered_photo || thumbnailUrl,
      thumbnail_url: thumbnailUrl,
      is_vehicle_detected: media.is_vehicle_detected,
      is_video: !!media.is_video,
      duration_in_seconds: media.duration_in_seconds,
    };
  });
}

function orderMedia(
  photos: InspectionPhotoDTO[],
  videos: VideoDTO[],
  type: 'pickup' | 'delivery',
): Array<
  InspectionPhotoDTO & { is_video?: boolean; duration_in_seconds?: number }
> {
  const sorted = sortPhotos(photos);

  const allMedia: Array<
    InspectionPhotoDTO & { is_video?: boolean; duration_in_seconds?: number }
  > = [];

  if (type === 'pickup' && sorted.length > 0) {
    const [firstPhoto] = sorted;
    if (firstPhoto) {
      allMedia.push({
        ...firstPhoto,
        type: firstPhoto.type || '',
        is_video: firstPhoto.is_video ?? false,
        duration_in_seconds: firstPhoto.duration_in_seconds ?? undefined,
      });
    }
  }

  if (videos.length > 0) {
    allMedia.push(...videos.slice(0, 1).map(parseVideo));
  }

  const remainingPhotos = type === 'pickup' ? sorted.slice(1) : sorted;
  allMedia.push(
    ...remainingPhotos.map((photo) => ({
      ...photo,
      type: photo.type || '',
      is_video: photo.is_video ?? false,
      duration_in_seconds: photo.duration_in_seconds ?? undefined,
    })),
  );

  if (videos.length > 1) {
    allMedia.push(...videos.slice(1).map(parseVideo));
  }

  return allMedia;
}

const PHOTO_IN_SYNC_PLACEHOLDER =
  'https://storage.googleapis.com/superdispatch-email-resources/photo-in-sync-placeholder.png';

export function parseVideo(
  video: VideoDTO,
): InspectionPhotoDTO & { is_video: boolean; duration_in_seconds?: number } {
  return {
    guid: video.guid || '',
    rendered_photo: video.url || '',
    rendered_photo_thumbnail: video.thumbnail_url || '',
    taken_at: null,
    type: video.type || '',
    is_vehicle_detected: null,
    damages: [],
    is_video: true,
    duration_in_seconds: video.duration_in_seconds || undefined,
    default_image: null,
  };
}

export function sortPhotos(photos: InspectionPhotoDTO[]) {
  const defaultImages: InspectionPhotoDTO[] = [];
  const requiredPhotos: InspectionPhotoDTO[] = [];
  const additionalPhotos: InspectionPhotoDTO[] = [];

  photos.forEach((photo) => {
    if (isDefaultInspectionPhoto(photo)) {
      defaultImages.push(photo);
    } else if (typeof photo.is_vehicle_detected === 'boolean') {
      requiredPhotos.push(photo);
    } else {
      additionalPhotos.push(photo);
    }
  });

  return [
    ...defaultImages,
    ...requiredPhotos.sort((a, b) => {
      return Number(!!a.is_vehicle_detected) - Number(!!b.is_vehicle_detected);
    }),
    ...additionalPhotos,
  ];
}

function buildVideoSlide(video: VideoDTO): Slide {
  return {
    type: 'video',
    width: 1280,
    height: 720,
    poster: video.thumbnail_url,
    autoPlay: true,
    sources: [{ src: video.url || '', type: 'video/mp4' }],
  };
}

function buildGalleryVideoSlide(
  video: VideoDTO,
  isPhotoSyncEnabled: boolean,
): Slide {
  if (
    isPhotoSyncEnabled &&
    !isInspectionPhotoSynced(getInspectableUrlsFromVideo(video))
  ) {
    return {
      src: PHOTO_IN_SYNC_PLACEHOLDER,
      alt: 'Awaiting for Sync with Driver App',
    };
  }

  return buildVideoSlide(video);
}

export function buildMediaSlides(
  photos: InspectionPhotoDTO[],
  videos: VideoDTO[],
  type: 'pickup' | 'delivery',
  isPhotoSyncEnabled: boolean,
): Slide[] {
  const sortedPhotos = sortPhotos(photos);

  const photoSlides: Slide[] = sortedPhotos.map((photo) => {
    const title =
      photo.is_vehicle_detected === false ? (
        <ViewLoadInspectionNonVehiclePhotoTitle />
      ) : undefined;

    if (
      isPhotoSyncEnabled &&
      !isInspectionPhotoSynced({
        url: photo.rendered_photo || '',
        thumbnailUrl: photo.rendered_photo_thumbnail || '',
      })
    ) {
      return {
        src: PHOTO_IN_SYNC_PLACEHOLDER,
        alt: 'Awaiting for Sync with Driver App',
        title,
      };
    }

    return {
      src: photo.rendered_photo || '',
      title,
    };
  });

  if (videos.length === 0) {
    return photoSlides;
  }

  const firstPhoto = type === 'pickup' ? photoSlides.slice(0, 1) : [];
  const remainingPhotos =
    type === 'pickup' ? photoSlides.slice(1) : photoSlides;
  const firstVideo = videos
    .slice(0, 1)
    .map((video) => buildGalleryVideoSlide(video, isPhotoSyncEnabled));
  const remainingVideos = videos
    .slice(1)
    .map((video) => buildGalleryVideoSlide(video, isPhotoSyncEnabled));

  return [...firstPhoto, ...firstVideo, ...remainingPhotos, ...remainingVideos];
}

export function buildFileUrls(
  photos: InspectionPhotoDTO[],
  videos: VideoDTO[],
  isPhotoSyncEnabled: boolean,
): string[] {
  const sortedPhotos = sortPhotos(photos);
  const imageUrls = isPhotoSyncEnabled
    ? sortedPhotos
        .filter((photo) =>
          isInspectionPhotoSynced({
            url: photo.rendered_photo || '',
            thumbnailUrl: photo.rendered_photo_thumbnail || '',
          }),
        )
        .map((photo) => photo.rendered_photo || '')
    : sortedPhotos.map((photo) => photo.rendered_photo || '');
  const videoUrls = isPhotoSyncEnabled
    ? videos
        .filter((video) =>
          isInspectionPhotoSynced(getInspectableUrlsFromVideo(video)),
        )
        .map((video) => video.url || '')
    : videos.map((video) => video.url || '');
  return [...imageUrls, ...videoUrls];
}
